import { useCallback, useMemo } from "react";
import type { RowData } from "@tanstack/react-table";
import { z } from "zod";
import { safeLocalStorage } from "@helpers/storage";
import {
    getDefaultColumnState,
    getLeafColumnInfos,
} from "../columns/columnLayout";
import type { DataGridColumn } from "../columns/types";
import type { DataGridColumnState } from "../types";
import { useStableColumnState } from "./useStableColumnState";

/** Bump when the stored shape changes; entries of other versions are ignored. */
const STORAGE_VERSION = 1;

const storedColumnStateSchema = z.object({
    version: z.literal(STORAGE_VERSION),
    columnState: z.object({
        order: z.array(z.string()),
        sizing: z.record(z.string(), z.number()),
        visibility: z.record(z.string(), z.boolean()),
        pinning: z.object({
            start: z.array(z.string()),
            end: z.array(z.string()),
        }),
        variants: z.record(z.string(), z.string()).optional(),
    }),
});

const parseStored = (raw: string | null) => {
    if (raw === null) {
        return undefined;
    }
    try {
        const parsed = storedColumnStateSchema.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data.columnState : undefined;
    } catch {
        return undefined;
    }
};

type StoredColumnState = NonNullable<ReturnType<typeof parseStored>>;

const pickEntries = <TValue>(
    record: Record<string, TValue>,
    isPicked: (id: string) => boolean,
) => Object.fromEntries(Object.entries(record).filter(([id]) => isPicked(id)));

/** The stored state with ids of columns not rendered now dropped. */
const readStoredColumnState = (
    key: string,
    knownIds: ReadonlySet<string>,
): DataGridColumnState | undefined => {
    const stored = parseStored(safeLocalStorage.getItem(key));
    if (!stored) {
        return undefined;
    }
    const isKnown = (id: string) => knownIds.has(id);
    return {
        order: stored.order.filter(isKnown),
        sizing: pickEntries(stored.sizing, isKnown),
        visibility: pickEntries(stored.visibility, isKnown),
        pinning: {
            start: stored.pinning.start.filter(isKnown),
            end: stored.pinning.end.filter(isKnown),
        },
        variants: pickEntries(stored.variants ?? {}, isKnown),
    };
};

/**
 * The table's state plus the stored entries of columns not rendered now, so
 * a column that renders later (a conditional or late column) keeps its width,
 * visibility, pin, variant, and place in the order.
 */
const withUnrenderedEntries = ({
    state,
    stored,
    knownIds,
}: {
    state: DataGridColumnState;
    stored: StoredColumnState;
    knownIds: ReadonlySet<string>;
}): DataGridColumnState => {
    const isUnrendered = (id: string) => !knownIds.has(id);
    const orderedIds = [...state.order];
    stored.order.forEach((id, index) => {
        if (isUnrendered(id)) {
            orderedIds.splice(Math.min(index, orderedIds.length), 0, id);
        }
    });
    return {
        order: orderedIds,
        sizing: {
            ...pickEntries(stored.sizing, isUnrendered),
            ...state.sizing,
        },
        visibility: {
            ...pickEntries(stored.visibility, isUnrendered),
            ...state.visibility,
        },
        pinning: {
            start: [
                ...(state.pinning.start ?? []),
                ...stored.pinning.start.filter(isUnrendered),
            ],
            end: [
                ...stored.pinning.end.filter(isUnrendered),
                ...(state.pinning.end ?? []),
            ],
        },
        variants: {
            ...pickEntries(stored.variants ?? {}, isUnrendered),
            ...state.variants,
        },
    };
};

interface UseStoredColumnStateInput<TData extends RowData> {
    /** localStorage key; one per table. */
    storageKey: string;
    /** The table's columns, so ids of removed columns are dropped. */
    columns: ReadonlyArray<DataGridColumn<TData>>;
}

/**
 * Keeps the table's column order, widths, visibility, pins, and variants in
 * localStorage under `storageKey`. Spread the result into `useDataGrid`.
 * Read when the key or the columns change; every change is written back,
 * keeping saved entries for columns not rendered now.
 */
export const useStoredColumnState = <TData extends RowData>({
    storageKey,
    columns,
}: UseStoredColumnStateInput<TData>) => {
    const leaves = useMemo(() => getLeafColumnInfos(columns), [columns]);
    const knownIds = useMemo(
        () => new Set(leaves.map((leaf) => leaf.id)),
        [leaves],
    );
    const columnState = useStableColumnState(
        useMemo(
            () =>
                readStoredColumnState(storageKey, knownIds) ??
                getDefaultColumnState(leaves),
            [storageKey, knownIds, leaves],
        ),
    );
    const onColumnStateChange = useCallback(
        (state: DataGridColumnState) => {
            const stored = parseStored(safeLocalStorage.getItem(storageKey));
            safeLocalStorage.setItem(
                storageKey,
                JSON.stringify({
                    version: STORAGE_VERSION,
                    columnState: stored
                        ? withUnrenderedEntries({ state, stored, knownIds })
                        : state,
                }),
            );
        },
        [storageKey, knownIds],
    );
    return { columnState, onColumnStateChange };
};
